import axios from "axios";
import { useMutation } from "react-query";

import { ROLES } from "../../../../constants/auth";
import { AppConfig } from "../../../../configs/config";
import settings from "../../../../configs/config_settings.json";

export const TV_LOGIN_ERROR = {
	INVALID_CREDENTIALS: "invalid-credentials",
	MISSING_PARAMS: "missing-params",
	INVALID_USER: "invalid-user",
	INVALID_ORGANIZATION: "invalid-organization",
	UNAUTHORIZED: "unauthorized",
	EXPIRED: "expired",
	ALREADY_COMPLETED: "already-completed",
	NOT_FOUND: "not-found",
	UNKNOWN: "unknown",
};

const KNOWN_BACKEND_ERRORS = new Set([
	TV_LOGIN_ERROR.MISSING_PARAMS,
	TV_LOGIN_ERROR.INVALID_USER,
	TV_LOGIN_ERROR.INVALID_ORGANIZATION,
	TV_LOGIN_ERROR.UNAUTHORIZED,
	TV_LOGIN_ERROR.EXPIRED,
	TV_LOGIN_ERROR.ALREADY_COMPLETED,
	TV_LOGIN_ERROR.NOT_FOUND,
]);

const buildError = (code, message) => {
	const err = new Error(message || code);
	err.code = code;
	return err;
};

const normalizeUserCode = (raw) => {
	const clean = (raw ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
	if (clean.length !== 6) return null;
	return `${clean.slice(0, 3)}-${clean.slice(3)}`;
};

const extractStatusCode = (data) => {
	if (!data) return null;
	if (data.status && data.status !== "ok") {
		return data.message || data.error || data.status;
	}
	if (data.error) return data.error;
	return null;
};

const loginUrl = () => `${AppConfig.BASE_URL}/login`;

const authenticate = async ({ email, password, organizationId }) => {
	const res = await axios.get(loginUrl(), {
		params: {
			action: "authenticate",
			eMail: email,
			password,
			organizationId,
			role: ROLES.USER,
		},
	});

	if (res?.data?.status !== "ok" || !res?.data?.user_id) {
		throw buildError(TV_LOGIN_ERROR.INVALID_CREDENTIALS);
	}

	return res.data.user_id;
};

const completeDeviceAuth = async ({ userCode, userId, organizationId }) => {
	const res = await axios.get(loginUrl(), {
		params: {
			action: "completeDeviceAuth",
			user_code: userCode,
			userId,
			organizationId,
		},
	});

	const errorCode = extractStatusCode(res?.data);
	if (errorCode) {
		const normalized = KNOWN_BACKEND_ERRORS.has(errorCode)
			? errorCode
			: TV_LOGIN_ERROR.UNKNOWN;
		throw buildError(normalized);
	}

	return res.data;
};

export const useCompleteTvLogin = () => {
	return useMutation(async ({ userCode, email, password, sessionUserId }) => {
		const organizationId = settings.organization.organizationId;

		const normalizedCode = normalizeUserCode(userCode);
		if (!normalizedCode) {
			throw buildError(TV_LOGIN_ERROR.MISSING_PARAMS);
		}

		let userId = sessionUserId;
		if (!userId) {
			userId = await authenticate({ email, password, organizationId });
		}

		return completeDeviceAuth({ userCode: normalizedCode, userId, organizationId });
	});
};
